import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MagnifyingGlassIcon from '~/components/atoms/icon/magnifyingGlass/MangnifyingGlassIcon';
import { useRenderingInfo } from '~/contexts/useRenderingInfo';
import { useConfig } from '~/core/configuration/configContext';
import { UpdateSearchQuery } from '~/core/useSearchState';
import useDebounce from '~/hooks/useDebounce';
import useInitialRender from '~/utils/useInitialRender';
import styles from './search-bar.module.scss';

export interface ISearchBarProps {
    updateSearchQuery: UpdateSearchQuery;
    searchQuery: string;
    autofocus?: boolean;
}

const SearchBar: React.FC<ISearchBarProps> = ({ updateSearchQuery, searchQuery, autofocus = true }) => {
    const intl = useIntl();
    const isInitialRender = useInitialRender();
    const serpInputRef = React.useRef<HTMLInputElement>(null);
    const [searchTerm, setSearchTerm] = React.useState(searchQuery);
    const config = useConfig();
    const debounceTime = config.layout.configuration.debounceTime;
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTime ?? 250);
    const { isMobileVersion } = useRenderingInfo();

    React.useEffect(() => {
        // TODO: why do we have autoFocus on <input> and also this JS focus()?
        if (serpInputRef?.current && isMobileVersion && autofocus) {
            serpInputRef?.current.focus();
        }
    }, []);

    React.useEffect(() => {
        if (isInitialRender) {
            return;
        }
        updateSearchQuery(debouncedSearchTerm);
    }, [debouncedSearchTerm, updateSearchQuery, isInitialRender]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'Enter':
                serpInputRef.current?.blur();
                break;
        }
    };

    return (
        <div className={styles.searchBarWrapper} data-testid="search-wrapper">
            <div className={styles.searchBarContent}>
                <label htmlFor="searchfield" id="searchfieldLabel" data-testid="search-input-label">
                    <FormattedMessage id="search_bar_title" defaultMessage="Napište, co hledáte" />
                </label>
                <div className={styles.searchBarInput}>
                    <input
                        ref={serpInputRef}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        value={searchTerm}
                        type="search"
                        id="searchfield"
                        autoFocus={autofocus}
                        maxLength={100}
                        autoComplete="off"
                        aria-labelledby="searchfieldLabel"
                        data-testid="search-input"
                        placeholder={`${intl.formatMessage({
                            id: 'search_bar_title',
                            defaultMessage: 'Napište, co hledáte',
                        })}`}
                    />
                    <MagnifyingGlassIcon className={styles.searchBarIcon} data-testid="search-input-icon" />
                </div>
            </div>
        </div>
    );
};

export default SearchBar;
